import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePageTraffic } from "@/lib/usePageTraffic";
import AppChip from "@/components/AppChip";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Search, X, ChevronRight, ChevronDown, Check, Loader2, Copy, Pencil, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import ChartV2 from "@/pages/ChartV2";
import { patientOf } from "@/lib/claimReadiness";
import { provOf, isGenerated, payerOf, estCharge } from "@/lib/createClaimsModel";
import { getNote } from "@/lib/noteClaim";
import { useChartTabs } from "@/lib/chartTabs";
import "@/styles/dictationv2.css";

const RANGES = [
  { v: "today", t: "Today" },
  { v: "7d", t: "Last 7 days" },
  { v: "30d", t: "Last 30 days" },
  { v: "all", t: "All time" },
];
const FIELDS = [
  { v: "patient", t: "Patient", ph: "Name, 3+ letters" },
  { v: "mrn", t: "MRN", ph: "MRN, 3+ characters" },
  { v: "payer", t: "Payer", ph: "Payer name" },
];
const SEGMENTS = [
  { v: "all", t: "All" },
  { v: "ready", lg: "Ready to submit", sh: "Ready", dot: "var(--indigo)" },
  { v: "gen", lg: "Claim generated", sh: "Generated", dot: "var(--green, #34C759)" },
];
const CREDS = new Set(["MD", "DO", "DPM", "NP", "PA", "PAC", "RN", "PHD", "DNP", "DDS", "OD", "APRN", "FNP", "MBBS"]);

const parseLocal = (v) => new Date(typeof v === "string" && v.length === 10 ? `${v}T00:00:00` : v);
const fmtDos = (iso) => {
  if (!iso) return "—";
  const d = parseLocal(iso);
  if (isNaN(d)) return "—";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString([], opts);
};
const moneyOf = (n) => { const v = Number(estCharge(n)) || 0; return v > 0 ? `$${v.toFixed(2)}` : null; };
const nameOf = (p) => p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Patient";
const initials = (nm) => (String(nm).split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("") || "PT").toUpperCase();
const mrnOf = (p) => p.mrn || p.patient_mrn || null;
const payerP = (p) => p.insurance?.payer_name || p.insurance?.carrier_name || p.payer_name || p.payer || null;
const dobOf = (p) => p.dob || p.date_of_birth || p.birth_date || null;
const fmtDob = (v) => { if (!v) return null; const s = String(v); const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[3]}/${m[1]}` : s; };
const visitId = (n) => n.visit_id || n.encounter_id || n.id;
const titleName = (s) => !s ? "" : String(s).split(/\s+/).map((w) => {
  const core = w.replace(/[.,]/g, "");
  if (CREDS.has(core.toUpperCase())) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}).join(" ");

// ── small outside-click popover hook ──
function usePop() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  return [open, setOpen, ref];
}

// ── note parsing (shared with editor model) ──
const SOAP_ORDER = [
  ["Chief complaint", "chief_complaint"],
  ["Subjective", "subjective"],
  ["Objective", "objective"],
  ["Assessment", "assessment"],
  ["Plan", "plan"],
];
const composeSoap = (vn) => SOAP_ORDER.filter(([, k]) => vn?.[k]).map(([lab, k]) => `${lab}:\n${vn[k]}`).join("\n\n");
const isHdr = (l) => /^[A-Z][^:]{1,60}:\s*$/.test(l.trim());
function parseNote(text) {
  const lines = String(text || "").split("\n");
  const secs = []; let cur = null;
  for (const raw of lines) {
    if (isHdr(raw)) { cur = { h: raw.trim().replace(/:\s*$/, ""), body: [] }; secs.push(cur); }
    else if (cur) cur.body.push(raw);
    else { cur = { h: "", body: [raw] }; secs.push(cur); }
  }
  return secs.map((s) => ({ h: s.h, body: s.body.join("\n").replace(/^\n+|\n+$/g, "") })).filter((s) => s.h || s.body.trim());
}
function NoteBody({ text }) {
  const secs = useMemo(() => parseNote(text), [text]);
  if (!secs.length) return <div className="none">No visit note captured.</div>;
  return secs.map((s, i) => (
    <div className="sec" key={i}>
      {s.h ? <div className="h">{s.h}</div> : null}
      <div className="b">{s.body}</div>
    </div>
  ));
}

// ── Patient picker inside the start card ──
function PatientPicker({ clientId, onStart }) {
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState(null);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(-1);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => { setChosen(null); setQ(""); setResults([]); }, [clientId]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    const query = q.trim();
    if (!clientId || !query) { setResults([]); setStatus("idle"); return; }
    const t = setTimeout(async () => {
      const my = ++reqRef.current;
      setStatus("loading");
      try {
        const { data } = await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: clientId, q: query } });
        if (my !== reqRef.current) return;
        setResults(Array.isArray(data?.results) ? data.results : []);
        setStatus("done");
      } catch { if (my === reqRef.current) { setResults([]); setStatus("done"); } }
    }, 300);
    return () => clearTimeout(t);
  }, [q, clientId]);

  const pick = (p) => { setChosen(p); setOpen(false); setQ(""); setResults([]); setSel(-1); };
  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "Enter") { const p = sel >= 0 ? results[sel] : results[0]; if (p) { e.preventDefault(); pick(p); } return; }
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
  };

  if (chosen) {
    const nm = nameOf(chosen), mrn = mrnOf(chosen), dob = fmtDob(dobOf(chosen));
    const meta = [mrn ? `MRN ${mrn}` : null, dob ? `DOB ${dob}` : null].filter(Boolean).join(" · ");
    return (
      <>
        <div className="dv2-pick">
          <span className="dv2-chip" data-testid="dictation-chosen-patient">
            <span className="av">{initials(nm)}</span>
            <span className="idc"><span className="nm">{nm}</span>{meta ? <span className="mt">{meta}</span> : null}</span>
            <button className="x" onClick={() => setChosen(null)} aria-label="Change patient" title="Change patient" data-testid="dictation-clear-patient"><X size={14} /></button>
          </span>
        </div>
        <button className="dv2-start-btn" onClick={() => onStart(chosen)} data-testid="dictation-start-btn"><Mic size={14} /> Start dictation</button>
      </>
    );
  }
  return (
    <>
      <div className="dv2-pick" ref={boxRef}>
        <div className="dv2-field">
          <Search size={15} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); setSel(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            placeholder="Patient name or MRN"
            role="combobox"
            aria-expanded={open && !!q.trim()}
            data-testid="dictation-patient-input"
            autoComplete="off"
          />
          {status === "loading" ? <Loader2 size={14} className="animate-spin" /> : null}
        </div>
        {open && q.trim() ? (
          <div className="dv2-menu" role="listbox" data-testid="dictation-patient-menu">
            {status === "loading" && !results.length ? (
              <div className="dv2-menu-msg">Searching…</div>
            ) : results.length === 0 ? (
              <div className="dv2-menu-msg" data-testid="dictation-patient-nomatch">No patients match “{q.trim()}”.</div>
            ) : results.slice(0, 7).map((p, i) => {
              const nm = nameOf(p), mrn = mrnOf(p), dob = fmtDob(dobOf(p)), pr = payerP(p);
              const meta = [mrn ? `MRN ${mrn}` : null, dob ? `DOB ${dob}` : null, pr].filter(Boolean).join(" · ") || "No MRN on file";
              return (
                <div key={p.chart_uuid || p.chart_id || p.mrn || i} role="option" aria-selected={sel === i} className={`dv2-opt ${sel === i ? "sel" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => pick(p)} data-testid={`dictation-patient-opt-${p.chart_uuid || p.mrn || i}`}>
                  <span className="av">{initials(nm)}</span>
                  <span className="idc"><div className="nm">{nm}</div><div className="mt">{meta}</div></span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      <button className="dv2-start-btn" disabled data-testid="dictation-start-btn"><Mic size={14} /> Start dictation</button>
    </>
  );
}

// ── toolbar: status segments · date menu + search cell ──
function Toolbar({ range, setRange, field, setField, query, setQuery, segment, setSegment, counts }) {
  const [dOpen, setDOpen, dRef] = usePop();
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const rangeLabel = RANGES.find((r) => r.v === range)?.t || "Today";
  const fieldLabel = FIELDS.find((f) => f.v === field)?.t || "Patient";
  const fieldPh = FIELDS.find((f) => f.v === field)?.ph || "";

  useEffect(() => {
    if (!searchOpen) return;
    const onDown = (e) => { if (searchRef.current && !searchRef.current.contains(e.target) && !query.trim()) setSearchOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [searchOpen, query]);
  useEffect(() => { if (searchOpen) inputRef.current?.focus(); }, [searchOpen]);
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const typing = /INPUT|TEXTAREA/.test(el?.tagName || "") || el?.isContentEditable;
      if (e.key === "/" && !typing) { e.preventDefault(); setSearchOpen(true); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const cycleField = () => { const i = FIELDS.findIndex((f) => f.v === field); setField(FIELDS[(i + 1) % FIELDS.length].v); inputRef.current?.focus(); };

  return (
    <div className="dv2-bar" data-testid="dictation-toolbar">
      <div className="dv2-seg" role="radiogroup" aria-label="Status" data-testid="dictation-segments">
        {SEGMENTS.map((s) => (
          <button key={s.v} role="radio" aria-checked={segment === s.v} className={segment === s.v ? "on" : ""} onClick={() => setSegment(s.v)} title={s.lg || s.t} data-testid={`dictation-seg-${s.v}`}>
            {s.dot ? <span className="dot" style={{ background: s.dot }} /> : null}
            {s.t ? s.t : <><span className="lg">{s.lg}</span><span className="sh">{s.sh}</span></>}
            {counts ? <span className="c">{counts[s.v]}</span> : null}
          </button>
        ))}
      </div>

      <span className="dv2-sp" />

      <div className="dv2-tg">
        <div ref={dRef} style={{ position: "relative", display: "flex" }}>
          <button className="dv2-date" aria-haspopup="menu" aria-expanded={dOpen} onClick={() => setDOpen((o) => !o)} title="Filtering by date of service" data-testid="dictation-date-trigger">
            <span className="f">Service</span> <span>{rangeLabel}</span> <ChevronDown size={13} />
          </button>
          {dOpen ? (
            <div className="dv2-pop" style={{ top: "calc(100% + 6px)", right: 0 }} role="menu" data-testid="dictation-date-menu">
              <div className="dv2-pop-lab">Range</div>
              {RANGES.map((r) => (
                <button key={r.v} role="menuitemradio" aria-checked={range === r.v} className={`dv2-mi ${range === r.v ? "sel" : ""}`} onClick={() => { setRange(r.v); setDOpen(false); }} data-testid={`dictation-date-${r.v}`}>
                  <span className="ck">{range === r.v ? <Check size={13} /> : null}</span><span className="lbl">{r.t}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div ref={searchRef} style={{ display: "flex", alignItems: "stretch" }}>
          {searchOpen ? (
            <div className="dv2-searchbox">
              <Search size={14} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") { if (query) setQuery(""); else { setSearchOpen(false); } } }}
                placeholder={fieldPh}
                aria-label="Search sessions"
                data-testid="dictation-search-input"
                autoComplete="off"
              />
              <button className="dv2-fchip" onClick={cycleField} title="Search by: Patient → MRN → Payer" aria-label={`Search field: ${fieldLabel}. Click to change`} data-testid="dictation-field-trigger">{fieldLabel} <ChevronDown size={11} /></button>
              <button className="dv2-sclr" onClick={() => { setQuery(""); setSearchOpen(false); }} aria-label="Clear search" title="Clear" data-testid="dictation-search-close"><X size={13} /></button>
            </div>
          ) : (
            <button className="dv2-search" onClick={() => setSearchOpen(true)} aria-label="Search sessions" title="Search · Patient, MRN or payer" data-testid="dictation-search-trigger"><Search size={15} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── expanded row detail ──
function SessionDetail({ n, onOpenClaim }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("custom");
  const { openNote } = useChartTabs();

  useEffect(() => {
    let live = true;
    (async () => { try { const d = await getNote(n.id); if (live && d) setDetail(d); } catch { /* keep */ } finally { if (live) setLoading(false); } })();
    return () => { live = false; };
  }, [n.id]);

  const enc = detail?.encounter || {};
  const soapObj = detail?.visit_note || n.visit_note || {};
  const customText = enc.formatted_note || enc.soap_text || detail?.visit_note_text || n.visit_note_text || composeSoap(soapObj);
  const soapText = composeSoap(soapObj) || customText;
  const shown = mode === "custom" ? customText : soapText;
  const icd = detail?.icd_codes || n.icd_codes || [];
  const cpt = detail?.cpt_codes || n.cpt_codes || [];
  const edited = !!(enc.formatted_manual_edit || enc.soap_manual_edit || enc.formatted_edited_at || enc.soap_edited_at);
  const mrn = mrnOf(n);

  const copy = async () => { try { await navigator.clipboard.writeText(shown || ""); toast.success("Note copied"); } catch { toast.error("Couldn't copy the note."); } };
  const edit = () => openNote?.({ noteId: n.id, name: patientOf(n), chartUuid: n.chart_uuid || n.chart_id || null, fromLabel: "Dictation" });

  return (
    <div className="dv2-exp" data-testid={`dictation-detail-${n.id}`}>
      <div className="dv2-exp-card">
        <div className="dv2-exp-bar">
          <div className="dv2-toggle" role="radiogroup" aria-label="Note view" data-testid={`dictation-note-toggle-${n.id}`}>
            <button role="radio" aria-checked={mode === "custom"} className={mode === "custom" ? "on" : ""} onClick={() => setMode("custom")} data-testid={`dictation-note-custom-${n.id}`}>Custom note</button>
            <button role="radio" aria-checked={mode === "soap"} className={mode === "soap" ? "on" : ""} onClick={() => setMode("soap")} data-testid={`dictation-note-soap-${n.id}`}>Standard SOAP</button>
          </div>
          {edited ? <span className="dv2-edited"><Check size={10} /> Edited</span> : null}
          <div className="dv2-exp-meta">
            <span>{titleName(provOf(n)) || "—"}</span>
            <span>{fmtDos(n.date_of_service)}</span>
            {mrn ? <span>MRN <span className="mono">{mrn}</span></span> : null}
            <span className="mono">{String(visitId(n)).slice(0, 12)}</span>
          </div>
          <div className="dv2-exp-acts">
            <button className="dv2-btn" onClick={copy} data-testid={`dictation-copy-${n.id}`}><Copy size={13} /> Copy note</button>
            <button className="dv2-btn" onClick={edit} data-testid={`dictation-edit-${n.id}`}><Pencil size={13} /> Edit note</button>
            <button className="dv2-btn pri" onClick={() => onOpenClaim(n)} data-testid={`dictation-open-claim-${n.id}`}>Open claim <ChevronRight size={13} /></button>
          </div>
        </div>
        <div className="dv2-xb">
          <div className="dv2-note" data-testid={`dictation-note-${n.id}`}>
            {loading && !detail ? <div className="dv2-note-loading"><Loader2 size={13} className="animate-spin" /> Loading the note…</div> : <NoteBody text={shown} />}
          </div>
          <div className="dv2-cds">
            <div>
              <h4>Diagnoses <span>{icd.length}</span></h4>
              {icd.length ? (
                <div className="dv2-cl" data-testid={`dictation-icd-${n.id}`}>
                  {icd.map((c, i) => (<code key={c.code + i} className={i === 0 ? "pri" : ""} title={c.description || c.code}>{c.code}</code>))}
                </div>
              ) : <div className="none">No diagnoses yet</div>}
            </div>
            <div>
              <h4>Procedures <span>{cpt.length}</span></h4>
              {cpt.length ? (
                <div className="dv2-cl" data-testid={`dictation-cpt-${n.id}`}>
                  {cpt.map((c, i) => (<code key={c.code + i} title={c.description || c.code}>{c.code}</code>))}
                </div>
              ) : <div className="none">No procedures yet</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ n, expanded, onToggle, onOpenClaim }) {
  const gen = isGenerated(n);
  const cpt = (n.cpt_codes || []).length, icd = (n.icd_codes || []).length;
  const payer = payerOf(n);
  const money = moneyOf(n);
  const dos = fmtDos(n.date_of_service);
  return (
    <div className={`dv2-sess ${expanded ? "exp" : ""}`} data-testid={`dictation-session-${n.id}`}>
      <div className={`dv2-grid dv2-row ${expanded ? "open" : ""}`} role="row" tabIndex={0} aria-expanded={expanded}
        onClick={() => onToggle(n.id)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(n.id); } }}
        data-testid={`dictation-session-row-${n.id}`}>
        <button className="dv2-chev" aria-label="Details" aria-expanded={expanded} tabIndex={-1} data-testid={`dictation-session-expand-${n.id}`}><ChevronRight size={15} /></button>
        <div className="dv2-pt">
          <div className="nm" title={patientOf(n)}>{patientOf(n)}</div>
          <div className="l2">
            <span className="vid">{String(visitId(n)).slice(0, 12)}</span>
            <span className="fold fold-dos">{dos}</span>
            <span className="fold fold-pay">{payer ? payer : <span className="noins">No insurance</span>}</span>
            <span className="fold fold-chg">{money || "No charge"}</span>
          </div>
        </div>
        <div className="dv2-cell col-dos">{dos}</div>
        <div className="dv2-cell col-prov" title={titleName(provOf(n))}>{titleName(provOf(n)) || "—"}</div>
        <div className="dv2-cell col-pay">
          {payer ? <span title={payer}>{payer}</span> : <span className="dv2-warn"><i /> No insurance</span>}
        </div>
        <div className="dv2-cell col-cod">{cpt + icd ? <span className="codes"><b>{cpt}</b> CPT · <b>{icd}</b> Dx</span> : <span className="dash">—</span>}</div>
        <div className="dv2-cell r col-chg">{money || <span className="dash">—</span>}</div>
        <div>
          <span className={`dv2-stat ${gen ? "gen" : "ready"}`} title={gen ? "Claim generated" : "Ready to submit"}><span className="d" /><span className="t">{gen ? "Claim generated" : "Ready to submit"}</span></span>
        </div>
      </div>
      {expanded ? <SessionDetail n={n} onOpenClaim={onOpenClaim} /> : null}
    </div>
  );
}

function Skeleton() {
  return (
    <>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div className="dv2-grid dv2-row" key={i} style={{ cursor: "default" }} aria-hidden="true">
          <span />
          <div className="dv2-pt"><div className="dv2-skel" style={{ width: `${48 + (i % 3) * 8}%` }} /><div className="dv2-skel" style={{ width: `${30 + (i % 3) * 6}%`, marginTop: 7, height: 8 }} /></div>
          <div className="dv2-cell col-dos"><div className="dv2-skel" style={{ width: "80%" }} /></div>
          <div className="dv2-cell col-prov"><div className="dv2-skel" style={{ width: "70%" }} /></div>
          <div className="dv2-cell col-pay"><div className="dv2-skel" style={{ width: "76%" }} /></div>
          <div className="dv2-cell col-cod"><div className="dv2-skel" style={{ width: "60%" }} /></div>
          <div className="dv2-cell col-chg"><div className="dv2-skel" style={{ width: "60%", marginLeft: "auto" }} /></div>
          <div><div className="dv2-skel" style={{ width: "70%" }} /></div>
        </div>
      ))}
    </>
  );
}

export default function Dictation() {
  const { selectedClientId } = useClient();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState("home");
  const [rows, setRows] = useState([]);
  const { report: reportTraffic } = usePageTraffic("dictation");
  const dictFirst = useRef(true);
  const [loading, setLoading] = useState(true);
  const [stripOn, setStripOn] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [range, setRange] = useState("7d");
  const [segment, setSegment] = useState("all");
  const [field, setField] = useState("patient");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const refreshRef = useRef(null);

  const load = useCallback(async (silent) => {
    if (!selectedClientId) { setRows([]); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/api/edi/note-to-claim/notes", { params: { client_id: selectedClientId, limit: 400, skip: 0 } });
      const notes = data?.notes || data?.items || [];
      const dict = notes.filter((n) => String(n.visit_source ?? n.source ?? "").toLowerCase().includes("dictation"));
      dict.sort((a, b) => new Date(b.created_at || b.date_of_service || 0) - new Date(a.created_at || a.date_of_service || 0));
      setRows(dict);
      if (!silent && dictFirst.current) { dictFirst.current = false; reportTraffic(dict.length); }
    } catch { /* keep prior rows */ } finally { if (!silent) setLoading(false); }
  }, [selectedClientId, reportTraffic]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(() => { if (!document.hidden && activeTab === "home") load(true); }, 30000); return () => clearInterval(iv); }, [load, activeTab]);

  // loading strip appears only after 400ms; elapsed timer while loading
  useEffect(() => {
    if (!loading) { setStripOn(false); setElapsed(0); return; }
    const t0 = Date.now();
    const show = setTimeout(() => setStripOn(true), 400);
    const iv = setInterval(() => setElapsed((Date.now() - t0) / 1000), 100);
    return () => { clearTimeout(show); clearInterval(iv); };
  }, [loading]);

  const refresh = () => {
    const el = refreshRef.current;
    if (el) { el.classList.remove("spin"); void el.offsetWidth; el.classList.add("spin"); }
    setOpenId(null);
    load();
  };

  const dateFiltered = useMemo(() => {
    if (range === "all") return rows;
    const now = new Date(); const start = new Date(now);
    if (range === "today") start.setHours(0, 0, 0, 0);
    else if (range === "7d") start.setDate(now.getDate() - 7);
    else if (range === "30d") start.setDate(now.getDate() - 30);
    return rows.filter((n) => { const t = parseLocal(n.date_of_service || n.created_at); return !isNaN(t) && t >= start; });
  }, [rows, range]);

  const counts = useMemo(() => {
    const gen = dateFiltered.filter(isGenerated).length;
    return { all: dateFiltered.length, gen, ready: dateFiltered.length - gen };
  }, [dateFiltered]);

  const filtered = useMemo(() => {
    let r = dateFiltered;
    if (segment === "gen") r = r.filter(isGenerated);
    else if (segment === "ready") r = r.filter((n) => !isGenerated(n));
    const term = query.trim().toLowerCase();
    if (term.length >= 3) {
      r = r.filter((n) => {
        const val = field === "mrn" ? String(mrnOf(n) || "") : field === "payer" ? String(payerOf(n) || "") : String(patientOf(n) || "");
        return val.toLowerCase().includes(term);
      });
    }
    return r;
  }, [dateFiltered, segment, query, field]);

  useEffect(() => { if (openId != null && !filtered.some((n) => n.id === openId)) setOpenId(null); }, [filtered, openId]);
  // close open row on Escape
  useEffect(() => {
    if (openId == null) return;
    const onKey = (e) => {
      const el = document.activeElement;
      const typing = /INPUT|TEXTAREA/.test(el?.tagName || "") || el?.isContentEditable;
      if (e.key === "Escape" && !typing) setOpenId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openId]);

  const start = (p) => {
    const uuid = p?.chart_uuid || p?.chart_id;
    if (!uuid) { toast.error("This patient has no chart to dictate into yet."); return; }
    const name = nameOf(p);
    setTabs((t) => t.some((x) => x.uuid === uuid) ? t : [...t, { uuid, name }]);
    setActiveTab(uuid);
  };
  const closeTab = (uuid) => { setTabs((t) => t.filter((x) => x.uuid !== uuid)); setActiveTab((a) => a === uuid ? "home" : a); load(true); };
  const openClaim = (n) => navigate(`/create-claims?claim=${encodeURIComponent(n.id)}&client=${encodeURIComponent(selectedClientId)}`);
  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  const rangeTail = range === "all" ? "any time" : range === "today" ? "today" : range === "7d" ? "in the last 7 days" : "in the last 30 days";

  return (
    <div className="h-full flex flex-col relative dv2" data-testid="dictation-page">
      {tabs.length > 0 ? (
        <div className="flex items-center gap-1 px-4 h-[42px] flex-none overflow-x-auto" style={{ borderBottom: "1px solid var(--hair)", background: "#fff" }} data-testid="dictation-tabs">
          <button onClick={() => setActiveTab("home")} data-testid="dictation-tab-home" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 h-[30px] rounded-[8px]" style={{ background: activeTab === "home" ? "var(--soft)" : "transparent", color: activeTab === "home" ? "var(--ink)" : "var(--sub)" }}>
            <Mic size={13} /> Dictation
          </button>
          {tabs.map((t) => (
            <div key={t.uuid} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 h-[30px] rounded-[8px]" style={{ background: activeTab === t.uuid ? "var(--ink)" : "#F7F7F6" }} data-testid={`dictation-tab-${t.uuid}`}>
              <button onClick={() => setActiveTab(t.uuid)} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: activeTab === t.uuid ? "#fff" : "var(--ink)" }}>
                <span className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center text-[9px] font-bold" style={{ background: activeTab === t.uuid ? "rgba(255,255,255,.18)" : "#E4E4E4", color: activeTab === t.uuid ? "#fff" : "var(--sub)" }}>{initials(t.name)}</span>
                {t.name}
              </button>
              <button onClick={() => closeTab(t.uuid)} aria-label="Close" data-testid={`dictation-tab-close-${t.uuid}`} className="w-[20px] h-[20px] rounded-[5px] inline-flex items-center justify-center" style={{ color: activeTab === t.uuid ? "rgba(255,255,255,.7)" : "var(--mute)" }}><X size={12} /></button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex-1 min-h-0 relative">
        <div style={{ display: activeTab === "home" ? "block" : "none" }} className="h-full overflow-y-auto">
          <div className="dv2-wrap">
            {/* 1 · page header */}
            <div className="dv2-head">
              <div className="dv2-head-l">
                <div className="dv2-trow"><h1 data-testid="dictation-title">Dictation</h1><AppChip app="lumina" /></div>
                <p className="dv2-sub">Dictate a visit and Lumina writes the SOAP note and codes the claim.</p>
              </div>
              <button ref={refreshRef} className="dv2-refresh" onClick={refresh} aria-label="Refresh" title="Refresh" data-testid="dictation-refresh"><RefreshCw size={16} /></button>
            </div>

            {/* 2 · start card */}
            <div className="dv2-start" data-testid="dictation-start-card">
              <div className="dv2-start-row">
                <div className="dv2-lead">
                  <strong>Dictate the visit, get a coded claim</strong>
                  <span className="lead-desc">Pick the patient and start. Works with one speaker or several, more than one language, or ambient listening.</span>
                </div>
                <PatientPicker clientId={selectedClientId} onStart={start} />
              </div>
            </div>

            {/* 3 · sessions */}
            <div className="dv2-card" data-testid="dictation-sessions">
              <Toolbar
                range={range} setRange={setRange}
                field={field} setField={setField}
                query={query} setQuery={setQuery}
                segment={segment} setSegment={setSegment}
                counts={loading ? null : counts}
              />

              <div className={`dv2-strip ${stripOn ? "open" : ""}`}>
                <div className="dv2-strip-in">
                  <div className="dv2-strip-row" data-testid="dictation-loading-strip">
                    <span className="dv2-pdot" /><span className="lab">Loading sessions</span><span className="el">{elapsed.toFixed(1)}s</span>
                  </div>
                </div>
              </div>

              <div className="dv2-grid dv2-hdr" role="row">
                <span />
                <span>Patient</span>
                <span className="col-dos">Date of service</span>
                <span className="col-prov">Provider</span>
                <span className="col-pay">Payer</span>
                <span className="col-cod">Coding</span>
                <span className="col-chg r">Charge</span>
                <span>Status</span>
              </div>

              {loading ? (
                <Skeleton />
              ) : filtered.length === 0 ? (
                <div className="dv2-empty" data-testid="dictation-sessions-empty">
                  <Mic size={22} />
                  {query.trim().length >= 3
                    ? <><div className="t">No sessions match {FIELDS.find((f) => f.v === field)?.t.toLowerCase()} “{query.trim()}”.</div><div className="s">Try a different term or clear the search.</div></>
                    : <><div className="t">No sessions in this view.</div><div className="s">Start one above or try a longer date range.</div></>}
                </div>
              ) : (
                <div className={`dv2-rows ${openId != null ? "focus" : ""}`}>
                  {filtered.map((n) => (
                    <Row key={n.id} n={n} expanded={openId === n.id} onToggle={toggle} onOpenClaim={openClaim} />
                  ))}
                </div>
              )}

              {!loading && filtered.length > 0 ? (
                <div className="dv2-foot" data-testid="dictation-foot">
                  {filtered.length} {filtered.length === 1 ? "session" : "sessions"} · date of service {rangeTail}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {tabs.map((t) => (
          <div key={t.uuid} style={{ display: activeTab === t.uuid ? "block" : "none" }} className="h-full">
            <ChartV2 embedded uuid={t.uuid} autoEncounter targetTab={{ tab: "focus" }} />
          </div>
        ))}
      </div>
    </div>
  );
}
